import { Box, ModalTypeMap, TextField } from "@mui/material";
import { useState } from "react";
import { AddModal } from "../../../components/molecules/AddModal";
import { Change, getDisplayText, getShortText } from "./changeChip";
import { DatePickerPopUp } from "../../../components/molecules/datepickers/datePickerPopUp";
import { DateTime } from "luxon";
import { BOMItem } from "./assetHistory";
import { PostToBackoffice } from "../../../utils/commands";
import { InputStyle } from "../../../components/atoms/inputs/style";

export const ServiceActionModal = ({
  open,
  closeModal,
  onSaved,
  modalType,
  item,
  parentItem,
  serialId,
  parts
}: {
  open: boolean,
  closeModal: Function,
  onSaved: (part: BOMItem) => void,
  modalType: Change,
  item: BOMItem,
  parentItem?: BOMItem,
  serialId: string,
  parts: BOMItem
}) => {
  const [savingState, setSavingState] = useState<"idle" | "saving" | "failed">("idle");
  const [date, setDate] = useState<DateTime>(DateTime.now())
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)
  const [addItemNumber, setAddItemNumber] = useState<string>()
  const [addItemName, setAddItemName] = useState<string>()

  const [description, setDescription] = useState<string>()
  const [quantity, setQuantity] = useState<string | undefined>("1.0")
  const [numberError, setNumberError] = useState<boolean>(false)

  const updateItemStatusRemove = (items: BOMItem, itemIdToRemove: string, parentId: string, parentOfItem?: BOMItem) : BOMItem => {
    const updatedItem = { ...items };

    if (parentOfItem && parentOfItem.partNumber === parentId && updatedItem.partNumber === itemIdToRemove) {
      updatedItem.change = "Removed"; 
    }

    if (updatedItem.children) {
      updatedItem.children = updatedItem.children.map(child => updateItemStatusRemove(child as BOMItem, itemIdToRemove, parentId, updatedItem));
    }
    return updatedItem;
  };

  const updateItemStatusAdd = (items: BOMItem, newItem: BOMItem, parentId: string) => {
    const updatedItem = { ...items };

    if (updatedItem.partNumber === parentId) {
      const newChild : BOMItem = { ...newItem, change:"Added"};
      updatedItem.children = [...(updatedItem.children || []), newChild]; 
    }

    if (updatedItem.children) {
      updatedItem.children = updatedItem.children.map(child => updateItemStatusAdd(child as BOMItem, newItem, parentId));
    }

    return updatedItem;
  };
  
  const handleChangeAdd = async () => {
    if(!addItemNumber || !addItemName || !quantity || quantity === "" || !description){
      setSavingState("failed")
      return
    }
      const body = {
        "id": serialId,
        "partNumber": addItemNumber,
        "partName": addItemName,
        "quantity": parseFloat(quantity),
        "unit": item.unit,
        "parentPartNumber": item.partNumber,
        "comment": description
      }

      setSavingState("saving")
      
      try {
          await PostToBackoffice('/cmd/hardware/machine/item/add', body)
          setSavingState("idle")
          const newItem :BOMItem= {
            partNumber: body.partNumber,
            name: body.partName,
            quantity: body.quantity,
            unit: body.unit,
            children: []
          }
          onSaved(updateItemStatusAdd(parts, newItem, item.partNumber))
          closeModal();

      } catch (error) {
          setSavingState("failed")
          console.error("cannot add item", error)
      }

    }

    const handleChangeRemove = async () => {
      if(!description || !parentItem){
        setSavingState("failed")
        return
      }
      const body = {
         "id": serialId,
        "itemNumber": item.partNumber,
        "itemName": item.name,
        "quantity": item.quantity,
        "unit": item.unit,
        "parentItemNumber": parentItem.partNumber,
        "comment": description
      }

      setSavingState("saving")
      
      try {
          await PostToBackoffice('/cmd/hardware/machine/item/remove', body)
          setSavingState("idle")
          onSaved(updateItemStatusRemove(parts, item.partNumber, parentItem.partNumber))
          closeModal();

      } catch (error) {
          setSavingState("failed")
          console.error("cannot remove item", error)
      }

    }

  return (
    <AddModal
      title={`${getDisplayText(modalType)} ${item.name}, ${item.partNumber}`}
      savingFailedMessage="'Saving failed. Please check your inputs below and your internet connection and try again.'"
      open={open}
      closeModal={closeModal}
      onSave={() => modalType === "Added" ? handleChangeAdd() : modalType === "Removed" ? handleChangeRemove() : console.log("replaced")}
      saving={savingState}
    >
      <Box sx={{display: "flex", flexDirection:"column", maxWidth: "200px"}}>
      <DatePickerPopUp
        date={date}
        onDatePicked={(date) => setDate(date)}
        open={openDatePicker}
        setOpen={setOpenDatePicker}/>
      </Box>
      {
        modalType === "Added" && (
          <Box sx={{display:"flex", flexDirection: "column"}}>
            <TextField
              id="article_number_input_field"
              margin="normal"
              type="text"
              fullWidth
              helperText={`Article number for the part you want to add`} 
              label="Article number"
              onChange={(event) => setAddItemNumber(event.target.value)}
              required
            />
            <TextField
              id="name__input_field"
              margin="normal"
              type="text"
              fullWidth
              helperText={"Name for the part you want to add"}
              label="Name"
              onChange={(event) => setAddItemName(event.target.value)}
              required
            />
            <Box sx={{maxWidth: "200px"}}>
             <TextField
                {...InputStyle}
                label={"Quantity"}
                type="number"
                fullWidth
                value={quantity}
                onChange={(e) => {
                  let val = e.target.value
                  setQuantity(val)
                  if (val === '' || isNaN(Number(val))){
                    setNumberError(true)
                  }
                  else{
                    setNumberError(false)
                  }
                }}
                required
                error={numberError}
                helperText= {numberError ? "Please provide a valid number" : `Quantity to ${getShortText(modalType)}`}
                InputProps={{
                  inputProps: {
                  step: "0.1",
                  min: 0,
                  },
                }}
                />
              
            </Box>
          </Box>
        )
      }
      <TextField
        id="name__input_field"
        margin="normal"
        type="text"
        fullWidth
        helperText={"What is the reason behind this change? "}
        label="Description"
        multiline
        onChange={(event) => setDescription(event.target.value)}
        required
      />
    </AddModal>
  )
}